/**
 * BulkRefreshPill — bottom-right floating pill that surfaces an in-progress
 * (or just-completed) bulk project metadata refresh, kicked off from
 * Settings → Project Maintenance. Renders nothing while idle.
 *
 * Mirrors the visual language of CacheStatusPill but is interactive: clicking
 * the pill reopens Settings and seeks to the maintenance section so the
 * user can see the per-project breakdown / dismiss the result.
 */
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useBulkRefreshStore } from '@/stores/bulkRefreshStore';
import { useUIStore } from '@/stores/uiStore';

export default function BulkRefreshPill() {
  const status = useBulkRefreshStore((s) => s.status);
  const done = useBulkRefreshStore((s) => s.done);
  const total = useBulkRefreshStore((s) => s.total);
  const currentName = useBulkRefreshStore((s) => s.currentName);
  const succeeded = useBulkRefreshStore((s) => s.succeeded);
  const skipped = useBulkRefreshStore((s) => s.skipped);
  const failed = useBulkRefreshStore((s) => s.failed);
  const openSettingsTo = useUIStore((s) => s.openSettingsTo);

  if (status === 'idle') return null;

  const handleClick = () => openSettingsTo('app-projects');
  // Keep the pill above the cache pill — both are bottom-right corners; we
  // shift the bulk-refresh pill up so they don't overlap.
  const positionClass = 'fixed bottom-14 right-4 z-40';

  if (status === 'running') {
    return (
      <button
        type="button"
        className={`${positionClass} group`}
        onClick={handleClick}
        title="View bulk-refresh progress"
      >
        <div className="flex items-center gap-2 bg-base-300/95 backdrop-blur border border-amber-400/40 rounded-full pl-3 pr-3 py-1.5 shadow-lg text-xs text-base-content/90 group-hover:border-amber-400/80 transition-colors">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />
          <span>
            Refreshing {Math.min(done + 1, total)}/{total}
            {currentName && <span className="text-base-content/60"> · {currentName}</span>}
          </span>
        </div>
      </button>
    );
  }

  // status === 'done'
  const hasFailures = failed > 0;
  const Icon = hasFailures ? AlertTriangle : CheckCircle2;
  const iconClass = hasFailures ? 'text-red-400' : 'text-emerald-400';
  const borderClass = hasFailures ? 'border-red-400/40' : 'border-emerald-400/40';

  return (
    <button
      type="button"
      className={`${positionClass} group`}
      onClick={handleClick}
      title="Open Project Maintenance"
    >
      <div className={`flex items-center gap-2 bg-base-300/95 backdrop-blur border ${borderClass} rounded-full px-3 py-1.5 shadow-lg text-xs text-base-content/90 group-hover:bg-base-300 transition-colors`}>
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} />
        <span>
          Refresh done — {succeeded} updated
          {skipped > 0 && <>, {skipped} skipped</>}
          {failed > 0 && <>, {failed} failed</>}
        </span>
      </div>
    </button>
  );
}
